import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  ITaskDenormalized,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { IOrganizationDenormalized } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { IUserDenormalized } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Button, IconButton, Typography } from '@material-ui/core';
import { truncate } from 'lodash';
import { CircleArrowLeft } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import ReactHelmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { Loader } from '../../../../../../components/Loader';
import { Spacer } from '../../../../../../components/Spacer';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useRouter } from '../../../../../../hooks/useRouter';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { Centered } from '../../../../../../layout/Centered';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import {
  useNumberQueryParam,
  useStringQueryParam,
  useTypedStringQueryParam
} from '../../../../../../routes';
import {
  SubTaskContext,
  TaskBody
} from '../../../../../features/Experience/components/Task';
import {
  getSubTaskContext,
  useTaskCompletionSuccessNotifications
} from '../../../../../features/Experience/service';
import { useProductAnalytics } from '../../../../../features/Experience/services/productAnalytics';
import { useStandaloneAppThemeOverride } from '../../../../../hooks/useStandaloneAppContext';
import { WithBreakpoints } from '../../components/Breakpoints';
import { EmployeeAppCard } from '../../components/EmployeeAppCard';
import { MessageCard } from '../../components/Messages';
import { InboxNavItemKey, InboxStyleNav } from '../../components/Nav/InboxNav';
import {
  getStartingWeek,
  groupWeeks,
  WeekSelector
} from '../../components/Nav/WeekNav';
import {
  PageContainer,
  PageContainerInner,
  PageFrame,
  PageFrameHeader
} from '../../components/PageFrame';
import { ParticipantAwareTitle } from '../../components/ParticipantAwareTitle';
import { ProgressTracker } from '../../components/ProgressTracker';
import {
  getUrlForItem,
  SearchWithinInstanceButton
} from '../../components/Search/WithinInstance';
import { DueOrCompletionDate } from '../../components/Tasks';
import { TaskStatusChipV2 } from '../../components/TaskStatusChipV2';
import { TopNavBarV2ForInstance } from '../../components/TopNav';
import { getAccompanyingMessage } from '../../services/items';
import { useEmployeeAppExperienceInstance } from '../../services/retrieval';
import {
  getIndividualInstanceUrl,
  getIndividualTaskUrl
} from '../../services/routes';

const Container = styled('div')((p) => ({
  width: '100%'
}));

const Main = ({
  instance,
  task,
  actorAccessToken,
  instanceAccessToken,
  returnTo,
  accompanyingMessage
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  task: ITaskDenormalized;
  actorAccessToken: string;
  instanceAccessToken: string;
  returnTo?: string;
  accompanyingMessage: IExperienceMessageDenormalized | undefined | null;
}) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const { t } = useUnsafeTranslation();
  const context = useMemo<{
    user: IUserDenormalized;
    userId: string;
    org: IOrganizationDenormalized;
    subTaskContext: SubTaskContext;
  }>(() => {
    return {
      user: instance.data.user,
      userId: instance.data.userId || '',
      org: instance.data.org,
      subTaskContext: getSubTaskContext(task.id, instance, trackEvent)
    };
  }, [instance, task, trackEvent]);

  const nextTask = useMemo(() => {
    const i = instance.data.tasks.findIndex((tsk) => tsk.id === task.id);
    // TODO decide how this should work.
    // currently trying to take the next incomplete task
    const remainingTasks = instance.data.tasks.slice(i + 1);
    return (
      remainingTasks.find(
        (tsk) =>
          tsk.status !== TaskStatus.COMPLETE &&
          tsk.status !== TaskStatus.NOT_STARTED
      ) || null
    );
  }, [instance.data.tasks, task]);

  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The parent element is the scroll container. When we move between tasks, go back to the top
    ref.current?.parentElement?.scrollTo(0, 0);
  }, [task.id]);

  const backUrl =
    returnTo || getIndividualInstanceUrl(actorAccessToken, instanceAccessToken);

  return (
    <Container ref={ref}>
      <FlexContainerVertical fullWidth spacing={3}>
        <FlexContainerVertical fullWidth spacing={2}>
          <Link
            to={backUrl}
            onClick={() =>
              trackEvent({
                eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
                name: 'Back Link Clicked',
                Explanation:
                  'A user clicked the back link at the top of the single task page',
                'Task ID': task.id,
                'Back URL': backUrl
              })
            }
          >
            <IconButton color="primary">
              <CircleArrowLeft size={24} />
            </IconButton>
          </Link>
          <WithBreakpoints
            lg={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                <Typography variant="h4">{task.title}</Typography>
                <FlexContainer>
                  <TaskStatusChipV2 status={task.status} />
                  <DueOrCompletionDate task={task} />
                </FlexContainer>
              </FlexContainer>
            }
            md={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                <Typography variant="h4">{task.title}</Typography>
                <FlexContainer>
                  <TaskStatusChipV2 status={task.status} />
                  <DueOrCompletionDate task={task} />
                </FlexContainer>
              </FlexContainer>
            }
            sm={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                <Typography variant="h4">{task.title}</Typography>
                <FlexContainer>
                  <TaskStatusChipV2 status={task.status} />
                  <DueOrCompletionDate task={task} />
                </FlexContainer>
              </FlexContainer>
            }
            xs={
              <FlexContainerVertical fullWidth spacing={1}>
                <Typography variant="h4">{task.title}</Typography>
                <FlexContainer>
                  <TaskStatusChipV2 status={task.status} />
                  <DueOrCompletionDate task={task} />
                </FlexContainer>
              </FlexContainerVertical>
            }
          />
          {accompanyingMessage && (
            <MessageCard
              d={accompanyingMessage}
              instanceAccessToken={instanceAccessToken}
            />
          )}
          <EmployeeAppCard>
            <TaskBody
              taskId={task.id}
              subtasks={task.blocks}
              context={context}
              noContainerMargins
            />
          </EmployeeAppCard>
        </FlexContainerVertical>
        {nextTask && (
          <Link
            to={getIndividualTaskUrl(
              actorAccessToken,
              instanceAccessToken,
              nextTask.id
            )}
          >
            <Button
              color="primary"
              variant={
                task.status === TaskStatus.COMPLETE ? 'contained' : 'outlined'
              }
            >
              {t('Next task')}: {truncate(nextTask.title, { length: 100 })}
            </Button>
          </Link>
        )}
      </FlexContainerVertical>
    </Container>
  );
};

type BodyProps = {
  instance: Doc<IExperienceDenormalizedV2>;
  taskId: string;
  actorAccessToken: string;
  instanceAccessToken: string;
  returnTo?: string;
};

const BodyInboxNavStyle = ({
  instance,
  taskId,
  actorAccessToken,
  instanceAccessToken,
  returnTo
}: BodyProps) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const task = useMemo(() => {
    return instance.data.tasks.find((tsk) => tsk.id === taskId);
  }, [instance.data.tasks, taskId]);

  const accompanyingMessage = useMemo(() => {
    return getAccompanyingMessage(instance.data.messages, task);
  }, [instance.data.messages, task]);
  const messagePresent = !!accompanyingMessage;

  const [selectedGroup] = useTypedStringQueryParam<InboxNavItemKey>(
    'g',
    task?.status === TaskStatus.COMPLETE || task?.status === TaskStatus.SKIPPED
      ? 'complete'
      : task?.status === TaskStatus.LATE
      ? 'overdue'
      : task?.status === TaskStatus.PAST_DEADLINE
      ? 'optional'
      : task?.status === TaskStatus.NOT_STARTED
      ? 'upcoming'
      : task?.status === TaskStatus.RUNNING
      ? 'todo'
      : 'everything'
  );

  useEffect(() => {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
      name: 'Single Task Page Loaded',
      Explanation: 'A user opened a task page',
      'Task ID': taskId,
      View: 'Status',
      'Message present': messagePresent
    });
  }, [trackEvent, taskId, messagePresent]);

  const { history } = useRouter();

  if (!task) {
    return <Centered>Task not found!</Centered>;
  }

  return (
    <PageContainer>
      <PageContainerInner>
        <TopNavBarV2ForInstance
          instance={instance}
          searchComponent={
            <SearchWithinInstanceButton
              allowGlobalSearch
              instance={instance}
              onSelectMatch={(item) => {
                const url = getUrlForItem(
                  item,
                  actorAccessToken,
                  instanceAccessToken,
                  returnTo
                );
                if (url) {
                  history.push(url);
                }
              }}
              closeOnSelect
            />
          }
        />
        <PageFrame
          aiDisclosure={instance.data.aiDisclosure}
          banner={instance.data.theming.banner}
          header={
            <PageFrameHeader>
              <ParticipantAwareTitle d={instance} />
              <ProgressTracker d={instance.data.progress} />
            </PageFrameHeader>
          }
          left={
            <InboxStyleNav
              actorAccessToken={actorAccessToken}
              instanceAccessToken={instanceAccessToken}
              instance={instance}
              g={selectedGroup}
              trackFn={trackEvent}
            />
          }
          center={
            <>
              <Main
                instance={instance}
                task={task}
                actorAccessToken={actorAccessToken}
                instanceAccessToken={instanceAccessToken}
                returnTo={returnTo}
                accompanyingMessage={accompanyingMessage}
              />
              <WithBreakpoints lg={<Spacer height={8} />} />
            </>
          }
          right={<div />}
        />
      </PageContainerInner>
    </PageContainer>
  );
};

const BodyWeekNavStyle = ({
  instance,
  taskId,
  actorAccessToken,
  instanceAccessToken,
  returnTo
}: BodyProps) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const weekGroups = useMemo(() => groupWeeks(instance), [instance]);
  const n = useMemo(() => moment(), []);
  const currentWeek = useMemo(
    () => getStartingWeek(instance, weekGroups, n),
    []
  );
  const [selectedWeek] = useNumberQueryParam('w', currentWeek);
  const task = useMemo(() => {
    return instance.data.tasks.find((tsk) => tsk.id === taskId);
  }, [instance.data.tasks, taskId]);

  const accompanyingMessage = useMemo(() => {
    return (
      task && instance.data.messages.find((m) => m.sendAt.isEqual(task.start))
    );
  }, [instance.data.messages, task]);
  const messagePresent = !!accompanyingMessage;

  useEffect(() => {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
      name: 'Single Task Page Loaded',
      Explanation: 'A user opened a task page',
      'Task ID': taskId,
      View: 'Timeline',
      'Message present': messagePresent
    });
  }, [trackEvent, taskId, messagePresent]);

  const { history } = useRouter();

  if (!task) {
    return <Centered>Task not found!</Centered>;
  }

  return (
    <PageContainer>
      <PageContainerInner>
        <TopNavBarV2ForInstance
          instance={instance}
          searchComponent={
            <SearchWithinInstanceButton
              allowGlobalSearch
              instance={instance}
              onSelectMatch={(item) => {
                const url = getUrlForItem(
                  item,
                  actorAccessToken,
                  instanceAccessToken,
                  returnTo
                );
                if (url) {
                  history.push(url);
                }
              }}
              closeOnSelect
            />
          }
        />
        <PageFrame
          aiDisclosure={instance.data.aiDisclosure}
          banner={instance.data.theming.banner}
          header={
            <PageFrameHeader>
              <ParticipantAwareTitle d={instance} />
              <ProgressTracker d={instance.data.progress} />
            </PageFrameHeader>
          }
          left={
            <WeekSelector
              instance={instance}
              weekGroups={weekGroups}
              currentWeek={currentWeek}
              selectedWeek={selectedWeek}
              setSelectedWeekToUrl={(w) =>
                getIndividualInstanceUrl(
                  actorAccessToken,
                  instanceAccessToken,
                  { w }
                )
              }
              n={n}
              trackFn={trackEvent}
            />
          }
          center={
            <>
              <Main
                instance={instance}
                task={task}
                actorAccessToken={actorAccessToken}
                instanceAccessToken={instanceAccessToken}
                returnTo={returnTo}
                accompanyingMessage={accompanyingMessage}
              />
              <WithBreakpoints lg={<Spacer height={8} />} />
            </>
          }
          right={<div />}
        />
      </PageContainerInner>
    </PageContainer>
  );
};

const Body = (props: BodyProps) => {
  if (props.instance.data.instancePageNavStyle === 'INBOX') {
    return <BodyInboxNavStyle {...props} />;
  }
  return <BodyWeekNavStyle {...props} />;
};

export const EmployeeAppPageExperienceTaskDetailsWithData = ({
  instance,
  actorAccessToken,
  instanceAccessToken,
  taskId
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  actorAccessToken: string;
  instanceAccessToken: string;
  taskId: string;
}) => {
  const [returnTo] = useStringQueryParam('returnTo');
  useStandaloneAppThemeOverride(instance?.data.org.experienceTheming || null);
  useTaskCompletionSuccessNotifications(instance);

  console.log('INSTANCE', instance, taskId);
  return (
    <>
      <ReactHelmet>
        <title>{instance.data.title}</title>
      </ReactHelmet>
      <Body
        instance={instance}
        taskId={taskId}
        actorAccessToken={actorAccessToken}
        instanceAccessToken={instanceAccessToken}
        returnTo={returnTo}
      />
    </>
  );
};

export const EmployeeAppPageExperienceTaskDetails = ({
  actorAccessToken,
  instanceAccessToken,
  taskId,
  v
}: {
  actorAccessToken: string;
  instanceAccessToken: string;
  taskId: string;
  v: number;
}) => {
  const [instance, loading, error] = useEmployeeAppExperienceInstance(
    actorAccessToken,
    instanceAccessToken,
    v
  );
  useErrorLogger('useEmployeeAppExperienceInstance', error);

  if (instance) {
    return (
      <EmployeeAppPageExperienceTaskDetailsWithData
        instance={instance}
        taskId={taskId}
        actorAccessToken={actorAccessToken}
        instanceAccessToken={instanceAccessToken}
      />
    );
  }

  // TODO error
  return <Loader />;
};
